'use client';
import {cn} from '@/lib/utils';
import {StrapiMilestoneEvent} from '@/types/strapi';
import {useLocale, useTranslations} from 'next-intl';
import {useEffect, useRef, useState} from 'react';

export default function Milestones(Prop: {moduleData: any; moduleOption: any}) {
  const [stopScroll, setStopScroll] = useState(false);
  const [isShowSideYearMenu, setIsShowSideYearMenu] = useState(false);
  const [isShowBottomYearMenu, setIsShowBottomYearMenu] = useState(false);
  const yearGroups = Prop?.moduleOption?.yearGroup || [1828, 1900, 1980, 2000];
  const [selectedYear, setSelectedYear] = useState(yearGroups[0]);
  const [currentItem, setCurrentItem] = useState('1828-0-0');
  const defaultOpacity = 'opacity-30';
  const locale = useLocale();
  const t = useTranslations('Milestone');
  const milestoneItems: {
    year: number;
    events: any[];
  }[] = [];

  Prop.moduleData.map((item: any) => {
    const year = parseInt(item.title.substring(0, 4));
    let yearGroup = yearGroups[0];
    yearGroups.map((group: number) => {
      if (year >= group) {
        yearGroup = group;
      }
    });
    if (!milestoneItems[yearGroup]) {
      milestoneItems[yearGroup] = {
        year: yearGroup,
        events: []
      };
    }
    milestoneItems[yearGroup].events.push(item);
  });

  const customStyle = `
  .contents .active highlight {
    font-weight: 700;
    color: #E12726;
    margin: 12px 0;
  }

  .contents .active {
    font-size: 18px;
  }

  .contents highlight {
    transition: opacity 0.3s ease-in-out;
  }

  @supports (animation-timeline: view()) {
  @media (pointer: fine) {
    .milestone-card {
      --offset: 500px;
    }
    .milestone-card:not(:first-child) {
      padding-top: var(--offset);
    }
    .milestone-card:not(:last-child) {
      animation: offsetPage linear forwards;
      animation-timeline: view();
      animation-range: calc(100% - 100vh) calc(100% + var(--offset));
      will-change: transform;
      backface-visibility: hidden;
    }
    @keyframes offsetPage {
      0% { transform: translate3d(0, 0, 0); }
      100% { transform: translate3d(0, calc(100vh + var(--offset)), 0); }
    }
  }
  }
  `;

  const [lastScrollY, setLastScrollY] = useState(0);
  const wrapperRef = useRef<any>(new Map());
  const getOffsetTop = (el: any) => {
    let offsetTop = 0;
    while (el) {
      offsetTop += el.offsetTop;
      el = el.offsetParent;
    }
    return offsetTop;
  };

  const [elementsInfo, setElementsInfo] = useState<{
    [key: string]: {top: number; bottom: number};
  }>({});

  const handleScroll = () => {
    const currentY = window.scrollY;
    const windowWidth = window.innerWidth;

    if (windowWidth > 1080) {
      if (currentY < lastScrollY && currentY > 1400) {
        setIsShowSideYearMenu(true);
      } else {
        setIsShowSideYearMenu(false);
      }
      setIsShowBottomYearMenu(false);
    } else {
      if (currentY < lastScrollY && currentY > 1400) {
        setIsShowBottomYearMenu(true);
      } else {
        setIsShowBottomYearMenu(false);
      }
      setIsShowSideYearMenu(false);
    }

    setLastScrollY(currentY);
    const taretOffset = window.innerHeight / 1.7;

    for (const [id, {top, bottom}] of Object.entries(elementsInfo)) {
      if (currentY >= top - taretOffset && currentY <= bottom - taretOffset) {
        if (!wrapperRef.current.get(id)?.dataset.inited) {
          wrapperRef.current.get(id)!.dataset.inited = true;
        }
        if (currentItem !== id) {
          setCurrentItem(id);
          setSelectedYear(parseInt(id.split('-')[0]));
        }
        break;
      }
    }
  };

  useEffect(() => {
    const calculateElementsInfo = () => {
      const info: {[key: string]: {top: number; bottom: number}} = {};
      if (wrapperRef.current) {
        wrapperRef.current.forEach((el: any, id: string) => {
          const top = getOffsetTop(el);
          const bottom = top + el.offsetHeight;
          info[id] = {top, bottom};
        });
      }
      setElementsInfo(info);
    };
    calculateElementsInfo();

    // const rect = s1.getBoundingClientRect();
    //const progress = Math.max(0, -rect.top) / window.innerHeight;
    //s2.style.transform = `translateY(${ (1 - progress) * 100 }vh)`;
    let stopTimer: string | number | NodeJS.Timeout | undefined;

    clearTimeout(stopTimer);
    if (!stopScroll) {
      window.addEventListener('scroll', handleScroll, {passive: true});
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      stopTimer = setTimeout(() => {
        const currentY = window.scrollY;
        if (lastScrollY == currentY) {
          setStopScroll(false);
          setCurrentItem(`${selectedYear}-0-0`);
        }
        setLastScrollY(currentY);
      }, 100);
    }
  }, [lastScrollY, stopScroll]);

  useEffect(() => {
    window.addEventListener('resize', handleScroll);
    const hash = window.location.hash;
    if (hash.startsWith('#item-')) {
      // activate the corresponding milestone card
      milestoneItems.map((item, iIndex) => {
        item?.events.map((event, jIndex) => {
          if (event.title === hash.replace('#item-', '')) {
            setCurrentItem(iIndex + '-' + jIndex + '-0');
          }
        });
      });
    }
  }, []);

  return (
    <div className="text-[18px]" id="milestone">
      <ul
        id="sideYearMenu"
        className={cn(
          'fixed hidden md:flex flex-col z-[200] right-[30px] top-[185px] text-left',
          'transition-all duration-500 ease-in-out gap-3 3xl:gap-5',
          isShowSideYearMenu ? 'opacity-100' : 'opacity-0 -z-[100]'
        )}
      >
        {yearGroups.map((year: number, i: number) => (
          <li
            key={i}
            className={cn(
              'px-1',
              selectedYear === year
                ? 'text-black list-disc'
                : 'text-[#888D90] list-[circle]'
            )}
          >
            <a
              href={`#group-${year}`}
              className={cn(
                ' w-[100px] text-center ubuntu ',
                selectedYear === year
                  ? 'text-black text-2xl font-[500] 3xl:text-3xl'
                  : 'text-[#888D90] text-xl 3xl:text-2xl'
              )}
              onClick={() => {
                setStopScroll(true);
                setSelectedYear(year);
              }}
            >
              {year}
            </a>
          </li>
        ))}
      </ul>

      <ul
        id="bottomYearMenu"
        className={cn(
          ' fixed flex z-[200] bottom-20 sm:bottom-10 text-left',
          'transition-all duration-500 ease-in-out ',
          'opacity-100 z-[100] bg-[#FDFDFDB2] px-4 py-2 rounded-full shadow-lg',
          'divide-x divide-gray-200 backdrop-blur-sm left-10',
          isShowBottomYearMenu ? 'opacity-100' : 'opacity-0'
        )}
      >
        {yearGroups.map((year: number, i: number) => (
          <li
            key={i}
            className={cn(
              'px-1',
              selectedYear === year ? 'text-black ' : 'text-[#888D90] '
            )}
          >
            <a
              href={`#group-${year}`}
              className={cn(
                ' w-[100px] text-center ubuntu text-base px-2',
                selectedYear === year
                  ? 'text-black font-[500] text-lg'
                  : 'text-[#484848]'
              )}
              onClick={() => {
                setStopScroll(true);
                setSelectedYear(year);
              }}
            >
              {year}
            </a>
          </li>
        ))}
      </ul>

      <div
        className={cn(
          'flex items-center justify-center transition-opacity duration-500 ease-in-out gap-2 md:gap-4 mb-[10vh] z-10 ubuntu text-sm px-3 md:px-0'
        )}
      >
        {yearGroups.map((year: number, i: number) => (
          <a
            key={i}
            className={cn(
              'rounded-full px-1 py-[10px] md:py-4 w-[100px] text-center',
              selectedYear === year
                ? 'bg-[#E22219] text-white font-[700]'
                : 'bg-[#FCEFEC] font-[400] hover:bg-[#FFD0CE]'
            )}
            href={`#group-${year}`}
            onClick={() => {
              setStopScroll(true);
              setSelectedYear(year);
            }}
          >
            {year}
          </a>
        ))}
      </div>
      <style>{customStyle}</style>
      <div
        className={cn(
          'flex flex-col transition-opacity duration-500 ease-in-out ',
          locale == 'en' && 'abhaya-libre'
        )}
      >
        {milestoneItems?.map((item: any, index: number) => {
          return (
            <div className="milestone-card relative z-2" key={index}>
              <div
                className={cn(
                  '-scroll-mt-[130px] md:-scroll-mt-[300px]',
                  'flex flex-col gap-4 relative bg-no-repeat observe-page',
                  ' rounded-tl-[120px] md:rounded-tl-[300px] overflow-hidden',
                  'transition-all duration-500 ease-in-out bg-white'
                )}
                style={{
                  backgroundSize: `100% 5046px`,
                  backgroundImage: `linear-gradient(178.84deg, rgba(255, 86, 86, 0.175) -36.25%, rgba(255, 255, 255, 0) 59.41%)`
                }}
                id={'group-' + item.year}
                data-id={item.year}
              >
                <div className="absolute h-[180px] md:h-[40vw] overflow-hidden w-full abhaya-libre">
                  <div className={cn('relative')}>
                    <div className="absolute flex justify-end items-start -right-[20] -top-[60px] md:-top-[13vw] font-[700] w-full">
                      <div
                        className="flex text-[180px] md:text-[40vw] leading-none text-transparent "
                        style={{
                          background:
                            'linear-gradient(to bottom, #B64744, #501F1E1A)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          opacity: 0.04
                        }}
                      >
                        <div>{item.year}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="flex justify-center items-center mb-4 mt-[146px] md:mt-[300px] z-10"
                  id={item.year}
                >
                  <h2
                    className={cn(
                      'text-[28px] leading-[45px] md:text-6xl font-bold md:p-2 md:mt-[50px] md:mb-[110px]',
                      'flex items-center justify-center',
                      'text-[#e21736] abhaya-libre'
                    )}
                  >
                    {item.year}
                  </h2>
                </div>

                <div className="container px-[24px] md:px-0 relative">
                  {item.events.map((milestone: any, jndex: number) => {
                    const id = `${index}-${jndex}`;
                    return (
                      <div
                        key={id}
                        className={cn('relative')}
                        id={'item-' + milestone.title}
                      >
                        {(milestone?.events ?? [])
                          .filter((a: any) => a.content)
                          .map((event: StrapiMilestoneEvent, kndex: number) => {
                            const eventId = `${id}-${kndex}`;
                            const isHide =
                              !(jndex == 0 && kndex == 0) &&
                              !wrapperRef.current?.get(eventId)?.dataset
                                ?.inited;
                            return (
                              <div
                                className={cn(
                                  'flex flex-col md:flex-row flex-rows-2 md:divide-x divide-gray-200 observe ',
                                  'relative border-l border-[#E0E0E0] pl-[20px] pb-[60px] md:p-0 md:border-l-0 gap-y-4',
                                  event.isReverse &&
                                    'md:flex-row-reverse md:divide-x-reverse'
                                )}
                                key={eventId}
                                ref={(node) => {
                                  if (node)
                                    wrapperRef.current.set(eventId, node);
                                  else wrapperRef.current.delete(eventId);
                                }}
                              >
                                <div
                                  className={cn(
                                    'contents md:block pt-0 pb-[120px] w-1/2 '
                                  )}
                                  id={eventId}
                                >
                                  <div className="contents md:block md:absolute left-1/2 right-1/2">
                                    <div className="contents md:block md:relative">
                                      <div
                                        data-id={eventId}
                                        className={cn(
                                          'absolute w-[8px] h-[40px] -left-[5px] md:-left-[4px] observe-mark z-10',
                                          ' transition-all duration-300 ease-in-out ',
                                          currentItem == eventId && !stopScroll
                                            ? 'bg-[#e21736]'
                                            : 'bg-[#AEAEAE] '
                                        )}
                                      ></div>
                                    </div>
                                  </div>
                                  <div
                                    className={cn(
                                      'contents md:block max-w-[330px] m-auto',
                                      'transition-all duration-500 ease-in-out',
                                      isHide
                                        ? 'translate-y-4 opacity-0'
                                        : `${defaultOpacity} md:opacity-100`,
                                      currentItem == eventId && !stopScroll
                                        ? 'opacity-100'
                                        : defaultOpacity
                                    )}
                                  >
                                    {!event.isHideTitle && (
                                      <div
                                        className={cn(
                                          index == milestone.title && 'hidden',
                                          'text-[24px] leading-[32px] md:text-3xl md:mb-6 md:text-black md:font-normal',
                                          'transition-all',
                                          currentItem == eventId && !stopScroll
                                            ? 'text-[#e21736] font-bold opacity-100'
                                            : defaultOpacity,
                                          'abhaya-libre'
                                        )}
                                      >
                                        {milestone.title}
                                      </div>
                                    )}
                                    <div
                                      className={cn(
                                        'text-black order-3 text-[16px]',
                                        'leading-[28px] transition-all',
                                        currentItem == eventId && !stopScroll
                                          ? 'active opacity-100'
                                          : defaultOpacity
                                      )}
                                      dangerouslySetInnerHTML={{
                                        __html: event.content
                                      }}
                                    ></div>
                                  </div>
                                </div>
                                {event.image && (
                                  <div
                                    className={cn(
                                      'w-full md:w-1/2 text-right md:pb-[120px] px-6'
                                    )}
                                    key={
                                      'event_' + milestone.title + '_' + kndex
                                    }
                                  >
                                    <div
                                      className={cn(
                                        'md:px-[5%] lg:px-0 md:max-w-[330px] m-auto group'
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          'relative',
                                          'transition-all duration-500 ease-in-out',
                                          isHide && 'translate-y-4 opacity-0',
                                          currentItem == eventId && !stopScroll
                                            ? 'opacity-100'
                                            : `${defaultOpacity}`
                                        )}
                                      >
                                        <img
                                          src={event.image}
                                          alt={milestone.title}
                                          className={cn(
                                            'md:absolute w-full z-[10] top-0 left-0 md:group-hover:-top-[10px] md:group-hover:-left-[10px] transition-all duration-300 ease-in-out',
                                            currentItem == eventId &&
                                              !stopScroll
                                              ? 'md:-top-[10px] md:-left-[10px]'
                                              : ''
                                          )}
                                        />
                                        <div
                                          className={cn(
                                            'hidden md:block z-10 top-0 w-full h-full object-cover bg-[#FF010099] opacity-100 '
                                          )}
                                          style={{
                                            backgroundBlendMode: 'overlay',
                                            backgroundImage: `url(${event.image})`,
                                            backgroundSize: 'cover'
                                          }}
                                        >
                                          <img
                                            src={event.image}
                                            alt={milestone.title}
                                            className={cn('w-full opacity-0')}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>

                {index < milestoneItems.length - 1 && (
                  <div className="w-full">
                    <div className="text-center  my-10 md:my-[80px] text-[#888D90] ubuntu font-[300]">
                      <div>{t('keepScrolling')}</div>
                      <div>
                        <span className="material-symbols thin">
                          keyboard_arrow_down
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
